// API-Zugriff: ein typisierter Client (aus der OpenAPI-Beschreibung generiert), ein Fehlerformat.
import createClient, { type Middleware } from "openapi-fetch";
import type { components, paths } from "./schema";

export type Schemas = components["schemas"];

export class ApiFehler extends Error {
  readonly code: number;
  readonly details: unknown;

  constructor(code: number, text: string, details?: unknown) {
    super(text);
    this.code = code;
    this.details = details;
  }
}

export const ABGEMELDET = "imho:abgemeldet";

let pruefung: Promise<void> | null = null;

/** Ist die eigene Sitzung wirklich weg? Eine 401 kann auch von einer Fremd-Schnittstelle kommen (Jira-Token abgelaufen,
 *  Kalender-Token) – das ist kein Sitzungsende. Nur wenn /api/auth/ich selbst 401 sagt, gilt man als abgemeldet. */
function sitzungPruefen(): void {
  pruefung ??= fetch("/api/auth/ich", { credentials: "same-origin", headers: { "X-IMHO": "1" } })
    .then((a) => { if (a.status === 401) window.dispatchEvent(new CustomEvent(ABGEMELDET)); })
    .catch(() => undefined)
    .finally(() => { pruefung = null; });
}

const kopf: Middleware = {
  onRequest({ request }) {
    // Schreibende Anfragen mit Sitzungs-Cookie brauchen diesen Kopf (Schutz gegen fremde Formulare).
    request.headers.set("X-IMHO", "1");
    return request;
  },
  onResponse({ response }) {
    if (response.status === 401) {
      const pfad = new URL(response.url, window.location.origin).pathname;
      if (pfad === "/api/auth/ich") window.dispatchEvent(new CustomEvent(ABGEMELDET));
      else if (pfad !== "/api/auth/anmelden") sitzungPruefen();
    }
    return response;
  },
};

export const api = createClient<paths>({ baseUrl: "", credentials: "same-origin" });
api.use(kopf);

type Antwort<T> = { data?: T; error?: unknown; response: Response };

/** Liefert die Daten oder wirft ApiFehler mit dem deutschen Text des Servers. */
export async function daten<T>(anfrage: Promise<Antwort<T>> | Antwort<T>): Promise<T> {
  const { data, error, response } = await anfrage;
  if (error !== undefined || !response.ok) {
    const f = (error as { fehler?: { code?: number; text?: string; details?: unknown } } | undefined)?.fehler;
    throw new ApiFehler(f?.code ?? response.status, f?.text ?? `Anfrage fehlgeschlagen (${response.status})`, f?.details);
  }
  return data as T;
}

export function fehlertext(e: unknown): string {
  if (e instanceof ApiFehler) return e.message;
  if (e instanceof TypeError) return "Server nicht erreichbar – Verbindung prüfen und erneut versuchen.";
  return e instanceof Error ? e.message : String(e);
}

/** PDF (oder ohne Datei) als Multipart senden – für Uploads, die openapi-fetch nicht typisiert. */
export async function pdfSenden<T>(pfad: string, datei?: File, felder: Record<string, string> = {}): Promise<T> {
  const form = new FormData();
  if (datei) form.append("datei", datei);
  for (const [k, v] of Object.entries(felder)) form.append(k, v);
  const a = await fetch(pfad, { method: "POST", body: form, headers: { "X-IMHO": "1" }, credentials: "same-origin" });
  const d = await a.json().catch(() => ({}));
  if (!a.ok) throw new Error(d?.fehler?.text ?? `Fehler ${a.status}`);
  return d as T;
}
